import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Alert, Avatar, Box, Button, Card, Chip, CircularProgress, LinearProgress,
  Paper, TextField, Typography,
} from "@mui/material";

const API_BASE = process.env.REACT_APP_COMPANY_API || "https://company-service.crediarc.com/api";
const analystBlue = "#0b2a4a";

export default function OfferLiftBorrowerPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [chatError, setChatError] = useState("");
  const [uploadBusy, setUploadBusy] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [messages, setMessages] = useState([]);
  const startedRef = useRef(false);
  const conversationEndRef = useRef(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await axios.get(`${API_BASE}/offerlift/public/${encodeURIComponent(token)}`);
        const publicCase = response.data?.data;
        if (!active) return;
        setData(publicCase);
        const existing = publicCase?.conversation || [];
        if (existing.length) {
          setMessages(existing);
          return;
        }
        if (startedRef.current) return;
        startedRef.current = true;
        setChatBusy(true);
        const startResponse = await axios.post(`${API_BASE}/offerlift/public/${encodeURIComponent(token)}/chat/start`);
        if (active) setMessages([{ role: "assistant", content: startResponse.data?.data?.message, createdAt: new Date().toISOString() }]);
      } catch (requestError) {
        if (active) setError(requestError?.response?.data?.message || "Could not open this OfferLift invitation");
      } finally {
        if (active) setChatBusy(false);
      }
    };
    load();
    return () => { active = false; };
  }, [token]);

  useEffect(() => { conversationEndRef.current?.scrollIntoView?.({ behavior: "smooth" }); }, [messages, chatBusy]);

  const sendMessage = async (suggestedMessage) => {
    const cleanMessage = String(suggestedMessage || message).trim();
    if (!cleanMessage || chatBusy) return;
    setMessages((current) => [...current, { role: "user", content: cleanMessage, createdAt: new Date().toISOString() }]);
    setMessage("");
    setChatError("");
    setChatBusy(true);
    try {
      const response = await axios.post(`${API_BASE}/offerlift/public/${encodeURIComponent(token)}/chat`, { message: cleanMessage });
      setMessages((current) => [...current, { role: "assistant", content: response.data?.data?.message || "I couldn't prepare an answer.", createdAt: new Date().toISOString() }]);
    } catch (requestError) {
      setChatError(requestError?.response?.data?.message || "The OfferLift Analyst is temporarily unavailable. Your answer has not been lost—please try again.");
    } finally { setChatBusy(false); }
  };

  const uploadDocument = async (requirement, file) => {
    if (!file || uploadBusy[requirement.id]) return;
    setUploadBusy((current) => ({ ...current, [requirement.id]: true }));
    setUploadErrors((current) => ({ ...current, [requirement.id]: "" }));
    try {
      const response = await axios.post(
        `${API_BASE}/offerlift/public/${encodeURIComponent(token)}/documents/${encodeURIComponent(requirement.id)}?fileName=${encodeURIComponent(file.name)}`,
        file,
        { headers: { "Content-Type": file.type }, timeout: 120000 },
      );
      setData(response.data?.data);
    } catch (requestError) {
      setUploadErrors((current) => ({ ...current, [requirement.id]: requestError?.response?.data?.message || "This document could not be analyzed. Please try again." }));
    } finally {
      setUploadBusy((current) => ({ ...current, [requirement.id]: false }));
    }
  };

  const statusPresentation = (status) => ({
    verified_consistent: { label: "Checks passed · human review required", color: "success" },
    needs_more_documents: { label: "More months required", color: "warning" },
    needs_review: { label: "Manual review required", color: "warning" },
    rejected: { label: "Not accepted", color: "error" },
    requested: { label: "Not uploaded", color: "default" },
  }[status] || { label: String(status || "Not uploaded").replaceAll("_", " "), color: "default" });

  if (!data && !error) return <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "#f4f7fb" }}><CircularProgress /></Box>;

  return <Box sx={{ minHeight: "100vh", bgcolor: "#f4f7fb", py: { xs: 2, md: 4 }, px: { xs: 1.5, md: 3 } }}>
    <Box sx={{ maxWidth: 1180, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ color: analystBlue, fontSize: { xs: 28, md: 34 }, fontWeight: 950, letterSpacing: "-.6px" }}>OfferLift</Typography>
          {data && <Typography sx={{ color: "#64748b", fontWeight: 700 }}>{data.borrowerName}</Typography>}
        </Box>
        <Chip label="Secure client conversation" variant="outlined" sx={{ bgcolor: "white", fontWeight: 800, color: analystBlue }} />
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : <>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 330px" }, gap: 2.25, alignItems: "start" }}>
          <Card variant="outlined" sx={{ borderRadius: 3, borderColor: "#dbe5ef", overflow: "hidden", boxShadow: "0 16px 45px rgba(11,42,74,.08)" }}>
            <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2, display: "flex", alignItems: "center", gap: 1.5, bgcolor: "white", borderBottom: "1px solid #e3ebf3" }}>
              <Avatar sx={{ bgcolor: "#1e6fe8", width: 44, height: 44, fontWeight: 900 }}>OA</Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: analystBlue, fontWeight: 950, fontSize: 17 }}>Your OfferLift Analyst</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: .7 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#16a34a" }} />
                  <Typography sx={{ color: "#64748b", fontSize: 12.5 }}>Online · reviewing your application with you</Typography>
                </Box>
              </Box>
            </Box>

            <Box aria-live="polite" sx={{ height: { xs: 470, md: 570 }, overflowY: "auto", px: { xs: 1.5, md: 2.5 }, py: 2.5, bgcolor: "#f8fbfe", display: "flex", flexDirection: "column", gap: 1.5 }}>
              {!messages.length && chatBusy && <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, color: "#64748b", my: "auto", mx: "auto" }}><CircularProgress size={20} /><Typography>Your analyst is joining the conversation…</Typography></Box>}
              {messages.map((item, index) => <Box key={`${item.role}-${item.createdAt || index}`} sx={{ display: "flex", gap: 1, alignItems: "flex-end", justifyContent: item.role === "user" ? "flex-end" : "flex-start" }}>
                {item.role === "assistant" && <Avatar sx={{ width: 28, height: 28, bgcolor: "#1e6fe8", fontSize: 11, fontWeight: 900 }}>OA</Avatar>}
                <Box sx={{ maxWidth: { xs: "88%", md: "76%" }, bgcolor: item.role === "user" ? "#1e6fe8" : "white", color: item.role === "user" ? "white" : analystBlue, px: 1.75, py: 1.35, borderRadius: item.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px", border: item.role === "assistant" ? "1px solid #dce7f1" : "none", boxShadow: item.role === "assistant" ? "0 3px 12px rgba(11,42,74,.06)" : "none" }}>
                  <Typography sx={{ whiteSpace: "pre-wrap", lineHeight: 1.58, fontSize: 15 }}>{item.content}</Typography>
                </Box>
              </Box>)}
              {messages.length === 1 && messages[0]?.role === "assistant" && <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", ml: 4.5 }}>
                <Button size="small" variant="outlined" onClick={() => sendMessage("I’m the owner and this is the correct business.")}>I’m the owner</Button>
                <Button size="small" variant="outlined" onClick={() => sendMessage("I handle finance for this business, and the company name is correct.")}>I handle finance</Button>
              </Box>}
              {chatBusy && messages.length > 0 && <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#64748b", ml: 4.5 }}><CircularProgress size={16} /><Typography fontSize={13}>The analyst is reviewing your answer…</Typography></Box>}
              <div ref={conversationEndRef} />
            </Box>

            <Box component="form" onSubmit={(event) => { event.preventDefault(); sendMessage(); }} sx={{ p: { xs: 1.5, md: 2 }, bgcolor: "white", borderTop: "1px solid #e3ebf3" }}>
              {chatError && <Alert severity="error" sx={{ mb: 1.25 }}>{chatError}</Alert>}
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                <TextField fullWidth multiline maxRows={4} placeholder="Type your answer or ask the analyst a question…" value={message} inputProps={{ maxLength: 1200, "aria-label": "Message your OfferLift Analyst" }} onChange={(event) => setMessage(event.target.value)} disabled={chatBusy} />
                <Button type="submit" variant="contained" disabled={chatBusy || !message.trim()} sx={{ minHeight: 56, px: 2.5, fontWeight: 900 }}>Send</Button>
              </Box>
              <Typography sx={{ color: "#64748b", fontSize: 11, mt: 1 }}>Do not share passwords, bank login credentials, card details, or government ID numbers in chat.</Typography>
            </Box>
          </Card>

          <Box sx={{ display: "grid", gap: 2 }}>
            <Alert severity="info" sx={{ borderRadius: 2.5 }}>{data.disclaimer}</Alert>
            <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3, borderColor: "#dbe5ef" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 1 }}>
                <Typography sx={{ fontWeight: 950, color: analystBlue }}>Requested information</Typography>
                <Chip size="small" label={`${data.progress?.percent || 0}% complete`} />
              </Box>
              <LinearProgress variant="determinate" value={data.progress?.percent || 0} sx={{ mb: 1.5, height: 7, borderRadius: 4 }} />
              {(data.requirements || []).map((item) => <Box key={item.id} sx={{ py: 1.5, borderTop: "1px solid #e3ebf3" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                  <Typography sx={{ fontWeight: 900, color: analystBlue, fontSize: 14 }}>{item.title}</Typography>
                  <Chip size="small" color={statusPresentation(item.status).color} label={statusPresentation(item.status).label} sx={{ height: "auto", maxWidth: 145, "& .MuiChip-label": { whiteSpace: "normal", textAlign: "center", py: .35, fontSize: 10.5 } }} />
                </Box>
                <Typography sx={{ color: "#64748b", fontSize: 12.5, mt: .35, lineHeight: 1.45 }}>{item.why}</Typography>
                {item.verificationSummary && <Alert severity={item.status === "rejected" ? "error" : item.status === "verified_consistent" ? "success" : "warning"} sx={{ mt: 1, py: 0, "& .MuiAlert-message": { fontSize: 11.5 } }}>{item.verificationSummary}</Alert>}
                {(item.documents || []).map((document) => <Box key={document.id} sx={{ mt: .8, px: 1, py: .75, bgcolor: "#f8fafc", borderRadius: 1.5 }}>
                  <Typography sx={{ color: analystBlue, fontSize: 11.5, fontWeight: 800, overflowWrap: "anywhere" }}>{document.fileName}</Typography>
                  {document.safeSummary && <Typography sx={{ color: "#64748b", fontSize: 11, mt: .25 }}>{document.safeSummary}</Typography>}
                </Box>)}
                {uploadErrors[item.id] && <Alert severity="error" sx={{ mt: 1, py: 0, "& .MuiAlert-message": { fontSize: 11.5 } }}>{uploadErrors[item.id]}</Alert>}
                <Button component="label" size="small" variant="outlined" disabled={Boolean(uploadBusy[item.id])} sx={{ mt: 1, fontWeight: 850 }}>
                  {uploadBusy[item.id] ? <><CircularProgress size={14} sx={{ mr: .8 }} />Analyzing document…</> : (item.documents || []).length ? "Upload another file" : "Upload and verify"}
                  <input hidden type="file" accept={(item.acceptedTypes || []).join(",")} onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; uploadDocument(item, file); }} />
                </Button>
              </Box>)}
              <Typography sx={{ color: "#64748b", fontSize: 11, mt: 1 }}>Automated checks look for document type, business-name match, date coverage, arithmetic consistency, and alteration signals. They cannot prove authenticity; every accepted file remains subject to human review.</Typography>
            </Paper>
            <Typography sx={{ color: "#64748b", fontSize: 12 }}>This secure invitation expires {new Date(data.expiresAt).toLocaleString()}.</Typography>
          </Box>
        </Box>
      </>}
    </Box>
  </Box>;
}
